import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';

/**
 * Service to manage Sentry error filters
 * This service provides methods to filter out specific error types
 * that are polluting the Sentry dashboard
 */
@Injectable({
  providedIn: 'root',
})
export class SentryFiltersService {
  // List of error patterns to ignore
  private errorPatternsToIgnore: RegExp[] = [
    // TypeError: D.forEach is not a function / C.forEach is not a function
    /[A-Z]\.(forEach|map|filter|reduce) is not a function/,

    // Error: NG02100
    /NG02100/,

    /Login Required/,

    /Cannot set properties of undefined (setting 'linear')/,
    /Cannot read properties of undefined (reading 'linear')/,

    // Common erros to be ignored
    /ResizeObserver loop/,
    /Failed to fetch/,
    /Network request failed/,
    /NetworkError/,
    /navigation cancelled/,
  ];

  // Lista de origens de erro para ignorar
  private sourcesToIgnore: string[] = [
    'node_modules/@angular/core',
    'node_modules/@angular/common',
    'node_modules/@angular/router',
    'polyfills.js',
  ];

  constructor() {
    this.setupGlobalFilters();
  }

  /**
   * Setup global filters for Sentry
   * Must be called during application initialization
   */
  setupGlobalFilters(): void {
    // Adds a global hooj for Sentry.beforeSend
    const client = Sentry.getCurrentHub().getClient();
    if (!client) {
      console.warn('Sentry client not available');
      return;
    }

    const originalBeforeSend = client.getOptions().beforeSend;

    client.getOptions().beforeSend = (event, hint) => {
      if (this.shouldIgnoreError(event)) {
        return null;
      }

      if (originalBeforeSend) {
        return originalBeforeSend(event, hint);
      }

      return event;
    };
  }

  /**
   * Verify if the error should be ignored based on the defined patterns
   */
  shouldIgnoreError(event: Sentry.Event): boolean {
    // Verify if the event has an excpetion
    if (
      !event.exception ||
      !event.exception.values ||
      event.exception.values.length === 0
    ) {
      return false;
    }

    const exception = event.exception.values[0];

    // Verifys the value of the error message
    if (exception.value) {
      for (const pattern of this.errorPatternsToIgnore) {
        if (pattern.test(exception.value)) {
          return true;
        }
      }
    }
    // Verify the origin of the error (stacktrace)
    if (exception.stacktrace && exception.stacktrace.frames) {
      for (const frame of exception.stacktrace.frames) {
        if (frame.filename) {
          for (const source of this.sourcesToIgnore) {
            if (frame.filename.includes(source)) {
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  /**
   * Adds a new pattern of error to be ignored
   */
  addErrorPatternToIgnore(pattern: RegExp): void {
    this.errorPatternsToIgnore.push(pattern);
  }

  /**
   * Adds a new origin of error to be ignored
   */
  addSourceToIgnore(source: string): void {
    this.sourcesToIgnore.push(source);
  }
}
